import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { PieChart } from 'lucide-react';
import { chartColors } from '../data/mockData';
import { useLanguage } from '../hooks/useLanguage';

const AllocationPieChart = ({ allocations, totalCapital, aiSplit }) => {
  const { t } = useLanguage();
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Calculate pie chart data
  const chartData = Object.entries(allocations).map(([basket, percentage]) => {
    const amount = totalCapital * (percentage / 100);
    return {
      basket,
      percentage,
      amount,
      color: chartColors[basket]
    };
  });

  // Create SVG pie chart
  const createPieChart = () => {
    const size = 280;
    const center = size / 2;
    const radius = size / 2 - 20;
    
    let cumulativePercentage = 0;
    
    return chartData.map((item, index) => {
      const percentage = item.percentage;
      const startAngle = (cumulativePercentage / 100) * 2 * Math.PI - Math.PI / 2;
      const endAngle = ((cumulativePercentage + percentage) / 100) * 2 * Math.PI - Math.PI / 2;
      
      const startX = center + radius * Math.cos(startAngle);
      const startY = center + radius * Math.sin(startAngle);
      const endX = center + radius * Math.cos(endAngle);
      const endY = center + radius * Math.sin(endAngle);
      
      const largeArcFlag = percentage > 50 ? 1 : 0;
      
      const pathData = `M ${center} ${center} L ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY} Z`;
      
      cumulativePercentage += percentage;
      
      return (
        <path
          key={item.basket}
          d={pathData}
          fill={item.color}
          stroke="#040816"
          strokeWidth="2"
          className="transition-all duration-300 cursor-pointer hover:opacity-80"
        />
      );
    });
  };

  const basketNames = {
    bitcoin: t('btcDca'),
    diversified: t('crypto'),
    aiTrading: t('aiTrading'),
    web3: t('web3')
  };

  return (
    <Card className="bg-[#0b1c38] border-[#12b7ff] shadow-xl">
      <CardHeader>
        <CardTitle className="text-[#00f4ff] flex items-center gap-2">
          <PieChart className="w-5 h-5" />
          {t('portfolioAllocation')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col lg:flex-row items-center gap-6">
          {/* Pie Chart */}
          <div className="relative flex-shrink-0">
            <svg width="280" height="280" className="transform rotate-0">
              {createPieChart()}
            </svg>
            
            {/* Center Total */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div 
                className="text-[22px] font-bold text-white"
                style={{
                  textShadow: '0 0 6px rgba(0,0,0,0.6), 0 0 12px rgba(0,0,0,0.4)',
                  color: '#FFFFFF'
                }}
              >
                {formatCurrency(totalCapital)}
              </div>
              <div 
                className="text-[12px] font-medium text-white"
                style={{
                  textShadow: '0 0 6px rgba(0,0,0,0.6)',
                  opacity: 0.85
                }}
              >
                {t('totalCapital')}
              </div>
            </div>
          </div>
          
          {/* Legend */}
          <div className="flex-1 space-y-3">
            {chartData.map((item) => (
              <div key={item.basket} className="flex items-center justify-between p-3 bg-[#040816] rounded-lg border border-[#0677aa]/30">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-4 h-4 rounded-full flex-shrink-0"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-white font-medium">
                    {basketNames[item.basket]}
                  </span>
                </div>
                <div className="text-right">
                  <div className="font-bold text-[#00f4ff]">
                    {formatCurrency(item.amount)}
                  </div>
                  <div className="text-sm text-gray-300">
                    {item.percentage.toFixed(1)}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* AI Trading Breakdown */}
        {allocations.aiTrading > 0 && (
          <div className="mt-6 p-4 bg-[#040816] rounded-lg border border-[#0677aa]/30">
            <h4 className="text-[#00f4ff] font-semibold mb-3">{t('aiTradingBreakdown')}</h4>
            <div className="space-y-2">
              {Object.entries(aiSplit).map(([bot, percentage]) => {
                const amount = totalCapital * (allocations.aiTrading / 100) * (percentage / 100);
                return (
                  <div key={bot} className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: chartColors[bot] }}
                      />
                      <span className="text-white capitalize">{bot}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-[#00f4ff] font-medium">
                        {formatCurrency(amount)}
                      </div>
                      <div className="text-xs text-gray-400">
                        {percentage.toFixed(1)}% {t('ofAI')}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AllocationPieChart;